// <synth-knob> — custom-drawn SVG rotary control.
//
// Light DOM so the global design tokens/theme apply. Pointer-drag (vertical),
// wheel, and keyboard accessible (role=slider); double-click resets to default;
// Shift = fine. Emits an `input` CustomEvent<number> on change. Configured
// programmatically via configure() (the UI binds it to a param schema entry).

import { normToValue, valueToNorm, formatValue, type Taper } from '../taper';

export interface KnobConfig {
  label: string;
  min: number;
  max: number;
  value: number;
  default: number;
  taper?: Taper;
  step?: number;
  unit?: string;
}

const START = -135;
const SWEEP = 270;
const R = 19;
const CX = 26;
const CY = 26;

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

function arc(from: number, to: number): string {
  const [x0, y0] = polar(CX, CY, R, from);
  const [x1, y1] = polar(CX, CY, R, to);
  const large = to - from > 180 ? 1 : 0;
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

export class Knob extends HTMLElement {
  private cfg: KnobConfig = { label: '', min: 0, max: 1, value: 0, default: 0 };
  private norm = 0;
  private dragging = false;
  private startY = 0;
  private startNorm = 0;

  private activeArc!: SVGPathElement;
  private indicator!: SVGLineElement;
  private readout!: HTMLDivElement;

  connectedCallback(): void {
    if (!this.isConnected || this.dataset.built) return;
    this.dataset.built = '1';
    this.build();
    this.render();
  }

  configure(cfg: KnobConfig): void {
    this.cfg = { taper: 'linear', step: 0, unit: '', ...cfg };
    this.norm = valueToNorm(cfg.value, cfg.min, cfg.max, this.cfg.taper);
    if (this.activeArc) this.render();
  }

  /** Update value from outside (e.g. preset load) without emitting. */
  setValue(value: number): void {
    this.cfg.value = value;
    this.norm = valueToNorm(value, this.cfg.min, this.cfg.max, this.cfg.taper);
    if (this.activeArc) this.render();
  }

  get value(): number {
    return this.cfg.value;
  }

  private build(): void {
    this.classList.add('knob');
    this.tabIndex = 0;
    this.setAttribute('role', 'slider');
    this.innerHTML = `
      <svg class="knob-svg" viewBox="0 0 52 52" aria-hidden="true">
        <path class="knob-track-active" fill="none" stroke-linecap="round"/>
        <circle class="knob-cap" cx="${CX}" cy="${CY}" r="13"/>
        <line class="knob-indicator" x1="${CX}" y1="${CY}" x2="${CX}" y2="14"/>
      </svg>
      <div class="knob-label"></div>
      <div class="knob-readout"></div>`;
    const svg = this.querySelector('.knob-svg')!;
    // Full-sweep background track, inserted behind the active arc.
    svg.insertBefore(
      this.makePath('knob-track-bg', arc(START, START + SWEEP)),
      svg.firstChild,
    );
    this.activeArc = this.querySelector('.knob-track-active') as SVGPathElement;
    this.indicator = this.querySelector('.knob-indicator') as SVGLineElement;
    this.readout = this.querySelector('.knob-readout') as HTMLDivElement;
    (this.querySelector('.knob-label') as HTMLDivElement).textContent = this.cfg.label;

    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('wheel', this.onWheel, { passive: false });
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('dblclick', this.onReset);
  }

  private makePath(cls: string, d: string): SVGPathElement {
    const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    p.setAttribute('class', cls);
    p.setAttribute('fill', 'none');
    p.setAttribute('stroke-linecap', 'round');
    p.setAttribute('d', d);
    return p;
  }

  private render(): void {
    const angle = START + this.norm * SWEEP;
    this.activeArc.setAttribute('d', arc(START, angle));
    const [x, y] = polar(CX, CY, R - 3, angle);
    this.indicator.setAttribute('x2', x.toFixed(2));
    this.indicator.setAttribute('y2', y.toFixed(2));
    (this.querySelector('.knob-label') as HTMLDivElement).textContent = this.cfg.label;
    this.readout.textContent = formatValue(this.cfg.value, this.cfg.unit ?? '');
    this.setAttribute('aria-valuemin', String(this.cfg.min));
    this.setAttribute('aria-valuemax', String(this.cfg.max));
    this.setAttribute('aria-valuenow', String(this.cfg.value));
    this.setAttribute('aria-label', this.cfg.label);
  }

  private setNorm(n: number, emit = true): void {
    this.norm = Math.min(1, Math.max(0, n));
    const value = normToValue(
      this.norm,
      this.cfg.min,
      this.cfg.max,
      this.cfg.taper,
      this.cfg.step,
    );
    this.cfg.value = value;
    this.render();
    if (emit) {
      this.dispatchEvent(new CustomEvent<number>('input', { detail: value }));
    }
  }

  private onPointerDown = (e: PointerEvent): void => {
    this.dragging = true;
    this.startY = e.clientY;
    this.startNorm = this.norm;
    this.setPointerCapture(e.pointerId);
    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerup', this.onPointerUp);
    e.preventDefault();
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.dragging) return;
    const fine = e.shiftKey ? 0.25 : 1;
    const delta = ((this.startY - e.clientY) / 180) * fine;
    this.setNorm(this.startNorm + delta);
  };

  private onPointerUp = (e: PointerEvent): void => {
    this.dragging = false;
    this.releasePointerCapture(e.pointerId);
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerup', this.onPointerUp);
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const fine = e.shiftKey ? 0.005 : 0.02;
    this.setNorm(this.norm - Math.sign(e.deltaY) * fine);
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    const fine = e.shiftKey ? 0.005 : 0.02;
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        this.setNorm(this.norm + fine);
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        this.setNorm(this.norm - fine);
        break;
      case 'Home':
        this.setNorm(0);
        break;
      case 'End':
        this.setNorm(1);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  private onReset = (): void => {
    this.setValue(this.cfg.default);
    this.dispatchEvent(new CustomEvent<number>('input', { detail: this.cfg.default }));
  };
}

customElements.define('synth-knob', Knob);
